import { creditsResetLabel, describeCredits } from '@shared/ai/credits';
import { type PlanId, planFor } from '@shared/plans';
import type { SessionState } from './Session';

/**
 * What the signed-in account is entitled to, in one word.
 *
 * ## Why a badge at all
 *
 * The commercial model is "free to use, paid to render": every tool that shapes a garden works on
 * every plan, and none of them is ever greyed out. Since phase 6 that is no longer quite the whole
 * story — how many sites an account may *keep*, and how large an image it may *take away*, do
 * depend on the plan — but the distinction holds and is the one worth defending. Nothing that makes
 * the design is gated; the ceilings are on storage and on export.
 *
 * Either way a user has no way to tell what they are entitled to until the moment something is
 * refused. The badge is the answer to "what do I have" asked *before* the wall rather than at it,
 * and the panel beneath it now spells out the three grants that are not credits.
 *
 * ## The word is the plan; the metal is the colour
 *
 * Since phase 3 the account has a real plan, reported on `UsageSummary`, and the badge says it:
 * **Free**, **Garden**, **Pro**. It used to say "Silver", which answered a question nobody asked —
 * a user knows what they subscribed to and wants to see that word, not a metaphor for it. Anonymous
 * keeps **Bronze**, because there is no plan to name: it is the original single-user tool, a
 * supported product rather than a lockout, and the badge reads as a grant.
 *
 * `tier` survives as the colour ramp only, and its rules are unchanged so no stylesheet moves:
 *
 * - **bronze** — no account. The full editor against this browser's storage, and no renders on the
 *   operator's key.
 * - **silver** — signed in against a metered allowance, whatever plan that allowance came from.
 * - **gold** — uncapped renders: either the account carries its own OpenAI key, or the deployment
 *   runs unmetered.
 *
 * A paid plan therefore changes the word and the number without changing the colour, which is the
 * right emphasis: what a user is about to run out of matters more to this badge than what they pay.
 *
 * ## What it deliberately does not count
 *
 * A key pasted into *this browser* buys uncapped AI too (see `AiGateway`, which posts straight to
 * OpenAI when there is no session), and it does not move the badge. A licence is what the account
 * grants; a key in `localStorage` is the user's own arrangement with OpenAI, and folding the two
 * together would have the app claim to have given someone something it did not. The panel shows the
 * key on its own line for exactly this reason — the two facts are independent and both are worth
 * knowing.
 */

export type LicenseTier = 'bronze' | 'silver' | 'gold';

export interface License {
  tier: LicenseTier;
  /** The word on the badge: the plan's name, or "Bronze" when there is no account to have a plan. */
  label: string;
  /**
   * The plan this account is on, or null when anonymous.
   *
   * Reported by the server rather than inferred from the allowance, which is the whole point: two
   * accounts can be one credit apart and on different plans, and only the server knows which.
   * Callers resolve it against `shared/plans.ts` for the project limit, export quality, and price.
   */
  plan: PlanId | null;
  /** One line naming what this tier grants, in the user's terms rather than the billing system's. */
  detail: string;
  /**
   * Credits left on the operator's key this month.
   *
   * Credits rather than renders, because renders are not comparable to each other: a wand cast and
   * a high-quality enhanced render both used to count as one, and they differ by roughly 330× in
   * what they cost to serve. A number that means something different every time it is decremented
   * is not a number anyone can plan against.
   *
   * Null when the question does not apply — anonymous (no allowance to draw on) or uncapped (no
   * ceiling to count down to).
   */
  creditsLeft: number | null;
}

/** Read the badge off a session. Pure, and safe to call on a session that has not resolved yet. */
export function licenseFor(state: SessionState): License {
  if (state.user === null) {
    return {
      tier: 'bronze',
      label: 'Bronze',
      detail:
        'The full editor, saved in this browser. Sign in to sync your sites and get AI renders on ' +
        'the included allowance.',
      plan: null,
      creditsLeft: null,
    };
  }

  const usage = state.usage;
  // A session that has not reported usage yet has no plan to name either, and Free is the honest
  // guess: it is what a new account is, and it is the one that promises least.
  const plan = planFor(usage?.plan);

  if (usage?.hasOwnKey === true) {
    return {
      tier: 'gold',
      label: plan.name,
      detail:
        `${plan.name} plan. AI renders run on your own OpenAI key, so there is no monthly cap and ` +
        'the usage appears on your OpenAI bill.',
      plan: plan.id,
      creditsLeft: null,
    };
  }

  if (usage !== null && usage.creditsLimit === null) {
    return {
      tier: 'gold',
      label: plan.name,
      detail: 'Sites synced to your account, and AI renders with no monthly cap on this deployment.',
      plan: plan.id,
      creditsLeft: null,
    };
  }

  // Signed in and metered. A session that has not reported usage yet lands here too, which is the
  // right guess: an account exists, and the allowance figure simply is not known this instant.
  const limit = usage?.creditsLimit ?? null;
  const creditsLeft =
    usage === null || limit === null ? null : Math.max(0, limit - usage.creditsUsed);
  return {
    tier: 'silver',
    label: plan.name,
    detail:
      creditsLeft === null
        ? `${plan.name} plan. Sites synced to your account, with a monthly allowance of AI credits.`
        : creditsLeft === 0
          ? // Zero is its own sentence, not the general one with a zero in it: the general form
            // renders "0 credits left this month — none left this month", which says the same
            // thing twice and buries the only part that helps, which is what to do about it.
            `${plan.name} plan. No AI credits left this month. They come back on ${creditsResetLabel(usage?.period)}` +
            ', or add your own OpenAI key below to lift the cap now.'
          : // The translation matters more than the number. "38 credits" is only meaningful to
            // someone who already knows what a credit buys, which is nobody on their first day.
            `${plan.name} plan. ${String(creditsLeft)} credit${
              creditsLeft === 1 ? '' : 's'
            } left this month — ${describeCredits(creditsLeft)}.`,
    plan: plan.id,
    creditsLeft,
  };
}
